import { useEffect, useState } from "react";
import { ResponsiveGridLayout, useContainerWidth } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAccess } from "@/context/AccessContext";
import { Briefcase, Users, Gavel, CheckSquare, Receipt, TrendingUp, GripVertical, RotateCcw, ShieldCheck, Wallet, Trophy } from "lucide-react";
import { Link } from "react-router-dom";
import { fmtDateTime } from "@/lib/time";

const STORAGE_KEY = "innoaim:dashboard-layout-v4";

const DEFAULT_LAYOUT = {
    lg: [
        { i: "open-cases", x: 0, y: 0, w: 4, h: 2 },
        { i: "clients", x: 4, y: 0, w: 4, h: 2 },
        { i: "hearings", x: 8, y: 0, w: 4, h: 2 },
        { i: "tasks", x: 0, y: 2, w: 4, h: 2 },
        { i: "outstanding", x: 4, y: 2, w: 4, h: 2 },
        { i: "ai", x: 8, y: 2, w: 4, h: 2 },
        { i: "next-hearings", x: 0, y: 4, w: 8, h: 4 },
        { i: "quick-actions", x: 8, y: 4, w: 4, h: 4 },
        { i: "compliance", x: 0, y: 8, w: 8, h: 4 },
        { i: "wallet", x: 8, y: 8, w: 4, h: 4 },
        { i: "ai-leaderboard", x: 0, y: 12, w: 12, h: 3 },
    ],
};

function Widget({ children, title, icon: Icon }) {
    return (
        <div className="bg-muted border border-border p-6 h-full overflow-hidden flex flex-col">
            <div className="flex items-start justify-between mb-3 select-none">
                <div className="overline">{title}</div>
                <div className="flex items-center gap-2 text-muted-foreground">
                    {Icon && <Icon className="w-4 h-4 text-primary" strokeWidth={1.5}/>}
                    <GripVertical className="w-3 h-3 widget-drag-handle cursor-move" strokeWidth={1.5}/>
                </div>
            </div>
            <div className="flex-1 min-h-0">{children}</div>
        </div>
    );
}

export default function TenantOverview() {
    const { user } = useAuth();
    const { width, containerRef, mounted } = useContainerWidth({ initialWidth: 1200 });
    const [stats, setStats] = useState(null);
    const [layouts, setLayouts] = useState(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            return saved ? JSON.parse(saved) : DEFAULT_LAYOUT;
        } catch { return DEFAULT_LAYOUT; }
    });

    useEffect(() => {
        api.get("/dashboard/stats").then(r => setStats(r.data)).catch(e => console.error("Failed to load dashboard stats", e));
    }, []);

    const onLayoutChange = (_curr, all) => {
        setLayouts(all);
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(all)); }
        catch (e) { console.warn("Could not persist dashboard layout to localStorage", e); }
    };

    const reset = () => {
        setLayouts(DEFAULT_LAYOUT);
        try { localStorage.removeItem(STORAGE_KEY); }
        catch (e) { console.warn("Could not clear dashboard layout from localStorage", e); }
    };

    return (
        <div className="space-y-8">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="overline mb-3">Overview</div>
                    <h1 className="font-serif text-4xl lg:text-5xl font-light">
                        Good day, <span className="italic">{user?.name?.split(" ")[0]}.</span>
                    </h1>
                    <p className="text-muted-foreground mt-3 text-sm">Drag any widget by the handle to rearrange. Layout is remembered per browser.</p>
                </div>
                <button onClick={reset} data-testid="dashboard-reset-btn" className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-2 border border-border px-3 py-2 transition-colors"><RotateCcw className="w-3 h-3" strokeWidth={1.5}/>Reset layout</button>
            </div>

            <div ref={containerRef}>
            {mounted && (
            <ResponsiveGridLayout
                className="layout"
                width={width}
                layouts={layouts}
                breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                cols={{ lg: 12, md: 12, sm: 6, xs: 4, xxs: 2 }}
                rowHeight={70}
                margin={[16, 16]}
                draggableHandle=".widget-drag-handle"
                onLayoutChange={onLayoutChange}
                isResizable
            >
                <div key="open-cases"><Widget title="Open cases" icon={Briefcase}>
                    <div className="font-serif text-5xl font-light">{stats?.open_cases ?? "—"}</div>
                    <div className="text-xs text-muted-foreground mt-2">of {stats?.total_cases ?? 0} total</div>
                </Widget></div>
                <div key="clients"><Widget title="Clients" icon={Users}>
                    <div className="font-serif text-5xl font-light">{stats?.total_clients ?? "—"}</div>
                </Widget></div>
                <div key="hearings"><Widget title="Hearings ahead" icon={Gavel}>
                    <div className="font-serif text-5xl font-light">{stats?.upcoming_hearings ?? "—"}</div>
                    <div className="text-xs text-muted-foreground mt-2">Court diary</div>
                </Widget></div>
                <div key="tasks"><Widget title="Open tasks" icon={CheckSquare}>
                    <div className="font-serif text-5xl font-light">{stats?.open_tasks ?? "—"}</div>
                </Widget></div>
                <div key="outstanding"><Widget title="Outstanding fees" icon={Receipt}>
                    <div className="font-serif text-5xl font-light">₹{(stats?.outstanding_fees ?? 0).toLocaleString()}</div>
                </Widget></div>
                <div key="ai"><Widget title="AI assistant" icon={TrendingUp}>
                    <div className="font-serif text-3xl font-light text-primary">Online</div>
                    <div className="text-xs text-muted-foreground mt-2">Claude Sonnet 4.5 default</div>
                </Widget></div>
                <div key="next-hearings"><Widget title="Upcoming hearings">
                    {stats?.next_hearings?.length ? (
                        <div className="divide-y divide-border overflow-y-auto max-h-full">
                            {stats.next_hearings.map(h => (
                                <div key={h.id} className="py-3 flex items-center justify-between text-sm">
                                    <div>
                                        <div className="font-mono text-xs text-muted-foreground">{fmtDateTime(h.scheduled_at)}</div>
                                        <div className="mt-1">{h.purpose || "Hearing"}</div>
                                    </div>
                                    <div className="text-xs text-muted-foreground">{h.court || "—"}</div>
                                </div>
                            ))}
                        </div>
                    ) : <div className="text-sm text-muted-foreground py-8 text-center">No hearings scheduled.</div>}
                </Widget></div>
                <div key="quick-actions"><Widget title="Quick actions">
                    <div className="space-y-2 overflow-y-auto max-h-full">
                        {[
                            { to: "/app/clients", label: "Add client" },
                            { to: "/app/cases", label: "Create litigation" },
                            { to: "/app/hearings", label: "Schedule hearing" },
                            { to: "/app/documents", label: "Upload document" },
                            { to: "/app/ai", label: "Ask AI" },
                        ].map(a => (
                            <Link key={a.to} to={a.to} className="block px-3 py-2 border border-border hover:border-primary hover:text-primary text-sm transition-colors">{a.label}</Link>
                        ))}
                    </div>
                </Widget></div>
                <div key="compliance"><Widget title="Compliance wall" icon={ShieldCheck}><ComplianceWall/></Widget></div>
                <div key="wallet"><Widget title="AI wallet" icon={Wallet}><WalletWidget/></Widget></div>
                <div key="ai-leaderboard"><Widget title="Cost-of-AI leaderboard (30 days)" icon={Trophy}><AiLeaderboard/></Widget></div>
            </ResponsiveGridLayout>
            )}
            </div>
        </div>
    );
}

function AiLeaderboard() {
    const [rows, setRows] = useState([]);
    const [denied, setDenied] = useState(false);
    useEffect(() => {
        api.get("/ai/leaderboard", { params: { days: 30, top: 5 } })
            .then(r => setRows(r.data || []))
            .catch(e => { if (e?.response?.status === 403) setDenied(true); });
    }, []);
    if (denied) return <div className="text-xs text-muted-foreground py-6 text-center">Only firm owner / managing partner can see the firm-wide AI leaderboard.</div>;
    if (!rows.length) return <div className="text-xs text-muted-foreground py-6 text-center">No AI usage in the last 30 days. Try asking the assistant something.</div>;
    const max = rows[0]?.cost_inr || 1;
    return (
        <div className="space-y-2 overflow-y-auto max-h-full" data-testid="ai-leaderboard">
            {rows.map((r, i) => (
                <div key={r.user_id || i} className="flex items-center gap-3" data-testid={`ai-leader-row-${i}`}>
                    <div className="w-5 text-center text-xs text-primary font-medium">{i + 1}</div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2">
                            <span className="text-sm truncate">{r.name}</span>
                            <span className="font-mono text-xs text-muted-foreground shrink-0">{r.calls} calls · ₹{r.cost_inr.toFixed(0)}</span>
                        </div>
                        <div className="h-1 bg-background mt-1.5">
                            <div className="h-1 bg-primary" style={{ width: `${Math.max(4, (r.cost_inr / max) * 100)}%` }}/>
                        </div>
                        {r.role && <div className="text-[10px] text-muted-foreground mt-0.5 capitalize">{r.role.replace(/_/g, " ")}</div>}
                    </div>
                </div>
            ))}
        </div>
    );
}


function ComplianceWall() {
    const [items, setItems] = useState([]);
    useEffect(() => {
        api.get("/audit/tenant", { params: { with_reason_only: true, limit: 20 } })
            .then(r => setItems(r.data || []))
            .catch(() => setItems([]));
    }, []);
    if (!items.length) return <div className="text-xs text-muted-foreground py-6 text-center">No reason-tagged changes yet — every edit with an X-Change-Reason header lands here for auditors.</div>;
    return (
        <div className="divide-y divide-border overflow-y-auto max-h-full" data-testid="compliance-wall">
            {items.map((a, i) => (
                <div key={a.id || i} className="py-2.5 flex items-start justify-between gap-3 text-xs">
                    <div className="flex-1 min-w-0">
                        {a.kind === "change" ? (
                            <>
                                <div className="font-mono text-[10px] text-muted-foreground">
                                    {a.entity?.toUpperCase()} · {(a.changes || []).map(c => c.field).join(", ").slice(0, 50)}
                                </div>
                                <div className="mt-0.5 text-muted-foreground">{a.user_email || "—"}</div>
                                {(a.changes || []).slice(0, 2).map((c, j) => (
                                    <div key={j} className="text-[10px] text-muted-foreground font-mono truncate">
                                        {c.field}: <span className="text-red-400/80">{String(c.old ?? "—").slice(0, 30)}</span> → <span className="text-emerald-400/80">{String(c.new ?? "—").slice(0, 30)}</span>
                                    </div>
                                ))}
                                <div className="mt-0.5 text-primary italic">"{a.reason}"</div>
                            </>
                        ) : (
                            <>
                                <div className="font-mono text-[10px] text-muted-foreground">{a.method} {String(a.path).slice(0, 60)}</div>
                                <div className="mt-0.5 text-muted-foreground">{a.user_email || "—"}</div>
                                <div className="mt-0.5 text-primary italic">"{a.reason}"</div>
                            </>
                        )}
                    </div>
                    <span className="font-mono text-[10px] text-muted-foreground shrink-0">{a.created_at?.slice(11, 16)}</span>
                </div>
            ))}
        </div>
    );
}

function WalletWidget() {
    const { can } = useAccess();
    const canTopup = can("wallet", "edit");
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/wallet").then(r => setData(r.data)).catch(() => setData(null));
    }, []);
    if (!data) return <div className="text-xs text-muted-foreground py-6 text-center">Loading…</div>;
    const low = data.balance < data.low_balance_threshold;
    return (
        <div className="flex flex-col h-full" data-testid="wallet-widget">
            <div className={`font-serif text-4xl font-light ${low ? "text-red-400" : ""}`}>₹{data.balance.toLocaleString()}</div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-[0.2em] mt-1">{data.currency} · AI wallet</div>
            {low && <div className="text-[10px] text-red-400 mt-2">Below ₹{data.low_balance_threshold} threshold</div>}
            <div className="mt-auto">
                {data.ai_usage_30d?.length > 0 && (
                    <div className="text-[10px] text-muted-foreground space-y-0.5 mb-3">
                        <div className="overline text-[9px]">30-day AI usage</div>
                        {data.ai_usage_30d.slice(0, 3).map(u => (
                            <div key={u.model} className="flex justify-between">
                                <span className="truncate mr-2">{u.model.replace(/-[\d]{8}$/, "")}</span>
                                <span className="font-mono">₹{u.cost_inr.toFixed(0)} · {u.calls}</span>
                            </div>
                        ))}
                    </div>
                )}
                {canTopup ? (
                    <Link to="/app/wallet" className="block px-3 py-1.5 border border-primary text-primary text-xs text-center hover:bg-primary hover:text-primary-foreground transition-colors" data-testid="wallet-topup-link">
                        Top up / Manage
                    </Link>
                ) : (
                    <Link to="/app/wallet" className="block px-3 py-1.5 border border-border text-muted-foreground text-xs text-center hover:text-muted-foreground transition-colors" data-testid="wallet-view-link">
                        View passbook
                    </Link>
                )}
            </div>
        </div>
    );
}
